/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Canopy is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Canopy is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Canopy, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Canopy:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Canopy broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Canopy utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Canopy only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Canopy.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Canopy is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Canopy provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

class ProductPrint extends HTMLElement {
  constructor() {
    super();

    this.enableLog = false;
  }

  connectedCallback() {
    this.log('Initialising', this);
    this.log('Config:', this.getConfig());
    this.elems = this.getElems();
    this.log('Elements:', this.elems);

    this.handleOpenModalListener = this.handleOpenModal.bind(this);
    this.elems.button.addEventListener('click', this.handleOpenModalListener);
  }

  handleOpenModal() {
    const { productHandle, variantId } = this.getConfig();
    const event = new CustomEvent('print-modal:open', {
      bubbles: true,
      detail: {
        productHandle,
        variantId,
      },
    });

    this.dispatchEvent(event);
  }

  getConfig() {
    return {
      productHandle: this.dataset.productHandle,
      variantId: Number(this.dataset.variantId),
    };
  }

  getElems() {
    return {
      /** @type {HTMLElement} */
      button: this.querySelector('.js-product-print-button'),
    };
  }

  log(...args) {
    if (!this.enableLog) return;
    console.log('\x1b[32m%s\x1b[9m', '[ProductPrint]', ...args);
  }
}

customElements.define('product-print', ProductPrint);

customElements.whenDefined('modal-dialog').then(() => {
  class QuickViewModal extends Modal {
    constructor() {
      super();
      const storedSections = sessionStorage.getItem('quick_view_sections');
      this.template = 'quick';
      this.sections = storedSections ? JSON.parse(storedSections) : [
        {
          id: null,
          section: 'main-product',
          selector: '.cc-main-product',
          classList: []
        },
        {
          id: null,
          section: 'alternative-products',
          selector: '.cc-product-recommendations',
          classList: []
        }
      ];
    }

    connectedCallback() {
      document.addEventListener('keyup', this.handleKeyUp.bind(this));
      document.addEventListener('quick-view:open', this.handleQuickViewOpen.bind(this));
      document.addEventListener('on:cart-drawer:before-open', this.handleDrawerOpen.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this.handleKeyUp.bind(this));
      document.removeEventListener('quick-view:open', this.handleQuickViewOpen.bind(this));
      document.removeEventListener('on:cart-drawer:before-open', this.handleDrawerOpen.bind(this));
    }

    handleDrawerOpen() {
      this.close();
    }

    handleKeyUp(e) {
      if (e.key === "Escape") {
        this.close();
      }
    }

    close() {
      super.close();
      const injectElement = this.querySelector('.js-modal-content');
      injectElement.classList.remove('product-main');
      injectElement.innerHTML = this.querySelector('.js-quickview-loading').innerHTML;
    }

    async handleQuickViewOpen(event) {
      const { productHandle, variantId } = event.detail;
      if (!productHandle || productHandle === '') return;
      this.open();

      await this.resolveProductSectionIDs(productHandle);

      const responseSections = await this.fetchProductQuickView(productHandle, variantId);

      this.querySelector('.js-modal-content').innerHTML =
        this.getSectionsToRender()
          .map(({ id, selector, classList }) => this.getElementHTML(responseSections[id], selector, classList))
          .join('');
    }

    async fetchProductQuickView(productHandle, variantId) {
      const searchParams = new URLSearchParams();
      this.getSectionsToRender().forEach(({ id }) => {
        searchParams.append('sections[]', id);
      });
      searchParams.append('view', this.template);
      // searchParams.append('variant', variantId);
      const response = await fetch(`${Shopify?.routes?.root ? Shopify.routes.root : `/`}products/${productHandle}?${searchParams.toString()}`);
      const json = await response.json();
      return json;
    }

    async resolveProductSectionIDs(productHandle) {
      if (this.sections[0].id != null) return;

      const pdpResponse = await fetch(`/products/${productHandle}?view=${this.template}`);
      const pdpResponseText = await pdpResponse.text();

      const tmpl = document.createElement('template');
      tmpl.innerHTML = pdpResponseText;

      this.sections = this.getSectionsToRender().map((element) => {
        const sectionElement = tmpl.content.querySelector(element.selector);
        return {
          ...element,
          id: sectionElement.id.replace('shopify-section-', ''),
          classList: [...sectionElement.classList]
        }
      });

      sessionStorage.setItem('quick_view_sections', JSON.stringify(this.sections));
    }

    getElementHTML(html, selector, classList) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const wrapper = document.createElement('div');
      const section = document.createElement('section');
      section.classList.add(...classList);
      try {
        section.innerHTML = tmpl.content.querySelector(selector).innerHTML;
        wrapper.appendChild(section);
        return wrapper.innerHTML;
      } catch (e) {
        sessionStorage.removeItem('quick_view_sections');
        location.reload();
      }
    }

    getSectionsToRender() {
      return this.sections;
    }
  }

  customElements.define('quickview-modal', QuickViewModal);

  class PrintModal extends Modal {
    constructor() {
      super();

      this.config = {};
      this.enableLog = false;
    }

    connectedCallback() {
      this.log('Initialising', this);
      this.elems = this.getElems();
      this.log('Elements:', this.elems);

      this.handleModalOpenListener = this.handleModalOpen.bind(this);
      document.addEventListener('print-modal:open', this.handleModalOpenListener);

      this.handleFormSubmitListener = this.handleFormSubmit.bind(this);
      this.elems.form.addEventListener('submit', this.handleFormSubmitListener);
    }

    handleModalOpen(event) {
      const { productHandle, variantId } = event.detail;
      this.config = {
        productHandle,
        variantId,
      };

      this.open();
    }

    async handleFormSubmit(event) {
      event.preventDefault();
      this.log('Form Submitting', event);

      this.elems.submitButton.disabled = true;
      this.elems.submitButton.setAttribute('aria-disabled', 'true');
      this.elems.submitButton.classList.add('is-loading');

      const formData = new FormData(event.target);
      const { information, label, nutritional_values, photo, quantities } = Object.fromEntries(formData);

      const printElements = {
        information: information === 'true',
        label: label === 'true',
        nutritional_values: nutritional_values === 'true',
        photo: photo === 'true',
        quantities: quantities === 'true',
      };

      const html = await this.fetchDocumentHTML(printElements);
      this.savePDF(html);
    }

    async fetchDocumentHTML(printElements) {
      this.log('Fetching PDF', printElements);
      const printableElementsString = Object.keys(printElements).join('+');
      const { productHandle, variantId } = this.config;
      const url = new URL(`${location.origin}/products/${productHandle}/${printableElementsString}`);
      url.searchParams.append('view', 'print');
      url.searchParams.append('variant', variantId.toString());

      const result = await fetch(url);
      const html = await result.text();

      return html;
    }

    async savePDF(html) {
      const parser = new DOMParser();
      const documentElement = parser.parseFromString(html, 'text/html');
      this.elems.printContent.innerHTML = documentElement.querySelector('main')?.innerHTML;
      const options = {
        margin: [0.5, 0.5, 0.5, 1],
        filename: `${this.config.productHandle}-${this.config.variantId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 1, useCORS: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      };

      const worker = window.html2pdf();
      worker.from(this.elems.printContent.querySelector('#PrintWrapper')).set(options).save();

      this.elems.submitButton.disabled = false;
      this.elems.submitButton.setAttribute('aria-disabled', 'false');
      this.elems.submitButton.classList.remove('is-loading');

      // this.close();
    }

    getElems() {
      return {
        /** @type {HTMLElement} */
        form: this.querySelector('.js-product-print-form'),
        /** @type {HTMLElement} */
        printContent: this.querySelector('.js-product-print-print-content'),
        submitButton: this.querySelector('button[type="submit"]'),
      };
    }

    log(...args) {
      if (!this.enableLog) return;
      console.log('\x1b[32m%s\x1b[9m', '[PrintModal]', ...args);
    }
  }

  customElements.define('print-modal', PrintModal);
});

function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.theme.settings.moneyWithCurrencyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    if (window.theme.settings.superscriptDecimals) {
      let val_split = `${dollarsAmount + centsAmount}`.split(decimal)
      return `${val_split[0]},<sup>${val_split[1]}</sup>`;
    }
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }

  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

window.Shopify.formatMoney = formatMoney;

class SoldOutWarning extends HTMLElement {
  constructor() {
    super();
    this.productId = parseInt(this.getAttribute('data-id'));
    this.data = JSON.parse(this.getAttribute('data-availibility'));
    this.container = this.closest('.product-info__add-to-cart, [is="cart-form"]');
  }

  connectedCallback() {
    document.addEventListener(`on:variant:change-${this.productId}`, this.handleVariantSelectChange.bind(this));
    document.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
  }

  handleVariantSelectChange(e) {
    let foundVariant = this.data.find((item) => item.variantId === e.detail.variant.id);
    const isAvailable = e.detail.variant.available && foundVariant.isAvailable === 'J';
    if (isAvailable) {
      this.setAttribute('hidden', 'true');
    } else {
      this.removeAttribute('hidden');
    }
  }

  handleVariantChange(e) {
    if (e.detail.product.id === this.productId) {
      let foundVariant = this.data.find((item) => item.variantId === e.detail.variant.id);
      let isVariantAvailable = foundVariant.isAvailable === 'J';
      if (isVariantAvailable) {
        this.setAttribute('hidden', 'true');
        this.container?.classList.remove('hide-children');
      } else {
        this.removeAttribute('hidden');
        this.container?.classList.add('hide-children');
      }
    }
  }
}
customElements.define('sold-out-warning', SoldOutWarning);

class CartCount extends HTMLElement {
  static elementName = 'cart-count';
  static events = {
    CART_UPDATE_SECTIONS: 'cart:on:update:sections',
  }

  constructor() {
    super();
    //console.log('🛒 CartCount element created');

    // document.addEventListener(CartCount.events.CART_UPDATE_SECTIONS, (event) => {
    //   console.log('🛒 CartCount received cart:on:update:sections event:', event.detail);
    //   const cartCountSectionHtml = event.detail['cart-icon-bubble'];
    //   if (cartCountSectionHtml) {
    //     this.handleCartUpdate(cartCountSectionHtml);
    //   }
    // });
  }

  connectedCallback() {
    //console.log('🛒 CartCount element connected to DOM');
  }

  handleCartUpdate(cartCountSectionHtml) {
      console.log('🛒 handleCartUpdate called with HTML:', cartCountSectionHtml);
    const div = document.createElement('div');
    div.innerHTML = cartCountSectionHtml;
      const cartCountEl = div.querySelector('cart-count');
      console.log('🛒 Found cart-count element in HTML:', cartCountEl);

      if (cartCountEl) {
        console.log('🛒 Updating innerHTML with:', cartCountEl.innerHTML);
        this.innerHTML = cartCountEl.innerHTML;
      } else {
      // Fallback to the old method
      const textEl = div.querySelector('.js-cart-count-text');
      console.log('🛒 Using fallback, found text element:', textEl);
      this.innerHTML = textEl?.innerHTML;
    }
  }
}

customElements.define(CartCount.elementName, CartCount);


class UserNumberUpdateForm extends HTMLElement {
  static elementName = 'user-number-update-form';
  static route = '/apps/stroetmann/api/v1/customers/set_current_user';
  static userNumbersOptionsSectionName = 'user-number-options';

  constructor() {
    super();
    this.enableLog = false;
    this._loading = false;
  }

  connectedCallback() {
    this.log('Initialising', this, { ...this });
    this.init();
  }

  async init() {
    const optionsEl = await this.fetchOptions();

    // ✅ Safari-safe innerHTML handling
    const temp = document.createElement('div');
    temp.innerHTML = `<form is="user-number-update-form" class="user-update-form">${optionsEl}</form>`;
    this.replaceChildren(...temp.childNodes);

    // Find the <form> inside this custom element
    const form = this.querySelector('form');
    if (!form) {
      console.log('[UserNumberUpdateForm] No <form> element found inside custom element.');
      return;
    }

    form.addEventListener('submit', this.handleSubmit.bind(this));
    const selectEl = form.querySelector('select');
    if (selectEl) {
      selectEl.addEventListener('change', this.handleSubmit.bind(this));
    }
  }

  async fetchOptions() {
    const url = new URL(`${location.origin}/cart`);
    url.searchParams.append('sections', UserNumberUpdateForm.userNumbersOptionsSectionName);

    const response = await fetch(url);
    const data = await response.json();

    return data[UserNumberUpdateForm.userNumbersOptionsSectionName];
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (this.loading) return;
    this.loading = true;

    const selectedOption = event.target?.selectedOptions[0];
    const current_user_alias = selectedOption?.dataset?.userAliasName || null;

    try {
      const response = await this.handleUpdateUserNumber(true,current_user_alias);
      if (!response.ok){
        throw new Error('Server error');
      }
      
      await this.awaitUserNumberChange(0);

      if (AssortmentCache) AssortmentCache.clear().catch(() => {});

      setTimeout(() => {
        location.reload();
      }, 300);

    } catch (error) {
      this.log('Could not set a new active User Number', error.message);
      this.loading = false;
    }
  }

  async awaitUserNumberChange(count = 0) {
    return new Promise(async (resolve) => {
      const fetchCart = await fetch('/cart?view=user_number', {
        headers: {
          'Content-Type': 'application/json',
          'Bypass-Internal': 'true',
          'No-Queue': 'true',
        },
      });
      const { current_user } = await fetchCart.json();
      
      if(!current_user) resolve();

      if (window.customer.currentUserNumber != current_user) {
        if (count > 0 && count % 5 === 0) {
          await this.handleUpdateUserNumber(true);
        }
        await new Promise((r) => setTimeout(r, 500));
        return this.awaitUserNumberChange(count++);
      }
      resolve();
    });
  }

  async handleUpdateUserNumber(forceUpdate = true, userAlias) {
    if (!forceUpdate) {
      document.getElementById('queue-warning-modal')?.open(this);
      return Promise.resolve();
    }

    const form = this.querySelector('form');
    if (!form) {
      console.log('[UserNumberUpdateForm] No <form> found for submission.');
      return Promise.resolve();
    }

    const { new_user_number: userNumber } = Object.fromEntries(new FormData(form));
    const { id: customerId, userName } = window.customer;
    const url = new URL(`${location.origin}${UserNumberUpdateForm.route}`);

    // reset checkout flag when is user number changes if checkout is alredy started.
    const cart = LocalCart.getState();
    if(cart.is_checkout_started){
      console.log('🔓 Reseting checkout flag due to user number change...');
      LocalCart.updateCheckoutState();
      await window.checkout?._clearAlternateItemsFromCart();
      await window.checkout?._clearItemProperties();
    }

    window.customer.currentUserNumber = userNumber;

    const formData = new FormData();
    if(userAlias) formData.append('attributes[current_user_alias]', userAlias);
    formData.append('attributes[current_user]', userNumber);
    formData.append('attributes[login_refresh]', 'true');
    formData.append('attributes[current_date]', '');
    formData.append('attributes[locked]', '');
    formData.append('attributes[bonus_amount_max]', '');
    formData.append('attributes[bonus_amount]', '');

    localStorage.setItem('current_step', '1');
    localStorage.removeItem('checkout_update_at');
    localStorage.removeItem('related_products');
    localStorage.removeItem('checkout_timeout');
    localStorage.removeItem('checkout_update_checkout_timeout');
    localStorage.removeItem('checkout_progress');
    localStorage.removeItem('checkout_approved');
    localStorage.removeItem('checkoutId');
    localStorage.removeItem('checkout');
    localStorage.removeItem('checkout_protection_active');
    localStorage.removeItem('checkout_lock_timestamp');
    localStorage.removeItem('checkout_started');
    localStorage.removeItem('checkout_shipping');
    localStorage.removeItem('shopify_local_cart');
    localStorage.removeItem('current_cart_token');
    
    removeLocalStorageItemsContaining('bonus');
    
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'No-Queue': 'true',
      },
      body: JSON.stringify({ customerId, userName, userNumber }),
    });

    await fetch('/cart/clear.js', {
      headers: {
        'Bypass-Internal': 'true',
        'No-Queue': 'true',
      },
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Bypass-Internal': 'true',
        'No-Queue': 'true',
      },
      body: formData,
    });
  }

  get loading() {
    return this._loading;
  }

  set loading(value) {
    this._loading = value;
    const eventName = value ? 'loading-curtain:show' : 'loading-curtain:hide';
    document.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: this }));
  }

  log(...args) {
    if (!this.enableLog) return;
    console.log('[UserNumberUpdateForm]', ...args);
  }
}

customElements.define(UserNumberUpdateForm.elementName, UserNumberUpdateForm);


class HeaderMenu extends HTMLElement {
  static elementName = 'header-menu';

  constructor() {
    super();

    this.open = false;
    this.accountMenu = document.querySelector('.account-menu');
    this.headerMenu = document.querySelector('header-menu');
  }

  connectedCallback() {
    document.addEventListener('click', this.toggleOpen.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.toggleOpen.bind(this));
  }

  toggleOpen({ target }) {
    if (this.open && this.accountMenu.contains(target) || !this.open && !this.headerMenu.contains(target)) return;

    this.open = !this.open;

    const operation = this.open ? 'add' : 'remove';
    this.classList[operation]('header__action--menu-open');
  }
}

customElements.define(HeaderMenu.elementName, HeaderMenu);

class CreateBredcrumbs extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.categories = JSON.parse(this.getAttribute('data-json'));
    this.tags = JSON.parse(this.getAttribute('data-tags'));
    this.buildBreadcrumbs(this.categories, this.tags);
  }

  buildBreadcrumbs(data, identifiers) {
    let sortedCategoriesArray = this.buildCategoryHierarchy(data, identifiers);
    if (sortedCategoriesArray.length) {
      this.navigationItems = sortedCategoriesArray;
      this.buildNavigationItems(this.navigationItems);
    }
  }

  buildCategoryHierarchy(data, identifiers) {
    // Step 1: Filter out "Category:" items
    const categories = identifiers
      .filter(id => id.startsWith("Category:"))
      .map(id => id.replace("Category:", ""));

    // Step 2: Find the correct S0 category
    let matchingS0Key = null;
    for (const key in data) {
      if (key.startsWith("S0-")) {
        const s0Category = data[key];
        for (const item of s0Category) {
          if (categories.includes(item.name) || categories.includes(item.handle)) {
            matchingS0Key = key;
            break;
          }
        }
      }
      if (matchingS0Key) break;
    }

    if (!matchingS0Key) {
      return []; // Return empty array if no match is found
    }

    // Step 3: Recursively build the hierarchy
    function findHierarchy(children, categoryList) {
      for (const child of children) {
        if (categoryList.includes(child.name) || categoryList.includes(child.handle)) {
          const subHierarchy = child.childs
            ? findHierarchy(child.childs, categoryList)
            : [];
          return [{ name: child.name, handle: child.handle }, ...subHierarchy];
        }
      }
      return [];
    }

    const s0Category = data[matchingS0Key];
    for (const topLevel of s0Category) {
      if (categories.includes(topLevel.name) || categories.includes(topLevel.handle)) {
        const hierarchy = findHierarchy(topLevel.childs || [], categories);
        return [{ name: topLevel.name, handle: topLevel.handle }, ...hierarchy];
      }
    }

    return [];
  }

  buildNavigationItems(itemsArray) {
    itemsArray.forEach((item) => {
      // Create a new list item
      let listNode = document.createElement('li');
      listNode.classList.add('flex', 'items-center');
      let svgItem = this.closest('ol').querySelector("svg");
      let svgClone = svgItem ? svgItem.cloneNode(true) : null; // Clone the SVG element to avoid moving it
      let anchorNode = document.createElement('a');
      anchorNode.classList.add('breadcrumbs-list__link');
      anchorNode.href = `/collections/${item.handle}`
      anchorNode.innerText = item.name;
      listNode.appendChild(anchorNode);

      if (svgClone) {
        listNode.appendChild(svgClone);
      }
      this.closest('ol').appendChild(listNode);
    })
  }
}

customElements.define("create-breadcrumbs", CreateBredcrumbs);



function handleCartClearConfirmation(event) {
  event.preventDefault();
  const confirmationText = event.target.dataset.confirmText; 
  const isConfirmed = window.confirm(confirmationText);
  if (isConfirmed) {
    // User clicked 'Yes' (OK)
    (async () => {
      try {
        event.target.classList.add('btn--loading');

        //Update the quantity-input value for this all variant inside a kios cached
        const { items } = LocalCart.getState();
        if(items.length){
          for (const itm of items) {
            if (AssortmentCache) {
              try {
                await AssortmentCache.patchCartQty(String(itm.product_id), String(itm.id), 0);
              } catch (e) {
                console.log(`Failed to patch assortment cached quantity input element`);
              }
            }
          }
        }
      
        LocalCart.clearLocalCart();
        // Local cart update event so latest cart saved to metafield
        if(sharedCart && Object.keys(sharedCart).length){
          await sharedCart.handleCartChange();
        }

        //refresh the assortment page if is open
        localStorage.setItem('tab_refresh_trigger', Date.now().toString());
        
        setTimeout(() => {
          window.location.reload();
        }, 200);
      } catch (error) {
        console.error("Failed to clear cart:", error);
        alert("There was an error clearing the cart. Please try again.");
      }
    })();
  }
}


function removeLocalStorageItemsContaining(substring) {
  // Get all keys currently in local storage
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // Check if the key contains the specified substring
    if (key && key.includes(substring)) {
      keysToRemove.push(key);
    }
  }

  // Remove the identified keys
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log(`Removed local storage item with key: ${key}`);
  });
}

function generateUniqueStringWithTimestamp() {
  const timestamp = Date.now(); // Get milliseconds since the Unix epoch
  const randomness = Math.random(); // Get a pseudo-random number between 0 and 1
  // Combine the timestamp and randomness into a unique string
  // Convert the timestamp to base 36 for compactness
  const uniqueId = timestamp.toString(36) + randomness.toString(36).substr(2);
  return uniqueId;
}

/*
  * Delete cached entry for assortment product 
  * @param {Array []} [productId]
*/
function deletCachedEntryForAssortmentProducts(Ids){
  // Single IDB transaction to delete all affected entries
  console.log(`Cached Product Entry Removed >>`, Ids)
  if (Ids.length && AssortmentCache) AssortmentCache.delMany(Ids).catch(() => {});
}

function logoutSession(event){
  event.preventDefault();
  event.target.setAttribute(`disabled`,`true`);
  window.clearCartAtributes(true);
  window.location.href = 'https://login.stroetmann24.com/auth/logout?return_to=/';
}

function wpLoginModal(event){
  event.preventDefault();

  const modal = document.getElementById('wp-login-modal');
  const iframe = document.getElementById('wp-login-iframe');
  const iframeSrc = event.target.getAttribute(`href`);
  const loader = modal.querySelector('.wp-login-loader');
  
  if(!iframeSrc && !modal && !iframe) return;

  if(loader) loader.classList.add('active');

  iframe.onload = function () {
    if(loader) loader.classList.remove('active');
  };

  iframe.src = iframeSrc;
  modal.setAttribute(`open`,`true`);
}

async function addItemsFromURL(){
  // Check if the URL contains "?add="
  const urlParams = new URLSearchParams(window.location.search);
  const addParam = urlParams.get("add");

  if (!addParam) return;

  try {
    // Parse items from ?add= param
    const decoded = decodeURIComponent(addParam);
    const data = JSON.parse(decoded);
    const items = data.items || [];

    if (!Array.isArray(items) || items.length === 0) {
      alert("No valid items found for adding to cart");
      window.location.href = '/pages/cart';
      return;
    }

    const cart = LocalCart.getState();
    let matchedItems = [];
    let remainingItems = [];

    if(cart.items.length){
      const itemsAlreadyInCart = new Map(cart.items.map(item => [String(item.id), item]));

      items.forEach(item => {
        const id = String(item.id);
        const match = itemsAlreadyInCart.get(id);

        if (match) {
          // Sum quantities (convert to number first)
          const itemsAlreadyInCartQty = Number(match.quantity || 0);
          const newQty = Number(item.quantity || 0);

          match.quantity = itemsAlreadyInCartQty + newQty;

          matchedItems.push(match);
        } else {
          remainingItems.push(item);
        }
      });
    }else{
      remainingItems = items;
    }

    console.log(`matchedItems >>`,matchedItems);
    console.log(`remainingItems >>`,remainingItems);

    //If items alredy in the cart
    if(matchedItems.length){
      const addingToCartAlertModal = document.querySelector(`adding-to-cart-alert-modal`);
      if(!addingToCartAlertModal) return;
      addingToCartAlertModal.setAttribute(`open`,true);

      if(addingToCartAlertModal.querySelector(`[data-cancel]`)){
        addingToCartAlertModal.querySelector(`[data-cancel]`).addEventListener(`click`,() => {
          window.location.href = '/pages/cart';
        });
      }

      if(addingToCartAlertModal.querySelector(`[data-proceed-add]`)){
        addingToCartAlertModal.querySelector(`[data-proceed-add]`).addEventListener(`click`, async () => {
          addingToCartAlertModal.removeAttribute(`open`);
          for(let i=0; i<matchedItems.length; i++){
            LocalCart.updateQuantity(matchedItems[i].id, matchedItems[i].quantity);
            //Update the quantity-input value for this variant inside a kios cached
            if (AssortmentCache) await AssortmentCache.patchCartQty(String(matchedItems[i].product_id), String(matchedItems[i].id), Number(matchedItems[i].quantity), matchedItems[i].key).catch(() => {});
          }
          await prepareATCFn(remainingItems);
        });
      }
      return;
    }

    await prepareATCFn(remainingItems);
    
  } catch (err) {
    console.error('Error adding items:', err);
  }
}

async function prepareATCFn(remainingItems){
  const addingToCartModal = document.querySelector(`adding-to-cart-modal`);
  if(addingToCartModal) addingToCartModal.setAttribute(`open`,true);


  for(let i=0; i<remainingItems.length; i++){
    await prepareAddToCartObject(remainingItems[i]?.handle, parseInt(remainingItems[i]?.id), remainingItems[i]?.quantity, remainingItems[i]?.properties);
  }    

  // Local cart update event so latest cart saved to metafield
  if(sharedCart && Object.keys(sharedCart).length){
      await sharedCart.handleCartChange();
  }

  //refresh the assortment page if is open
  localStorage.setItem('tab_refresh_trigger', Date.now().toString());

  // Redirect to cart page after successful add
  setTimeout(() => {
    window.location.href = '/pages/cart';
  }, 150);
}


async function prepareAddToCartObject(handle, variantId, quantity, properties){
  //console.log(`===================`)
  if(!handle) return;
  if(!variantId || isNaN(variantId)) return;
  const productRequest = await fetch(`/products/${handle}?view=custom-add`);
  const data = await productRequest.json();
  console.log(`data >>`,data)
  const product = data?.product;
  const selected_variant = data?.formatted[variantId];

  //console.log(`product >>`,product)
  //console.log(`selected_variant >>`,selected_variant)

  const extractedVariants = Object.values(data?.formatted).map(variant => ({
    id: variant.variant_id,
    title: variant.variant_title,
    sku: variant.identifier,
    featured_image: variant.featured_image,
    options: variant.variant_options,
    price: variant.variant_price,
    compare_at_price: variant.variant_compare_at_price,
    discounted_price: variant.variant_price,
    deposit_reference: variant.deposit_reference,
    netto_weight_sales_unit: variant.netto_weight_sales_unit,
    orderable: variant.isAvailable,
    bonus_product: variant.bonus_product,
    europe1_price_factory_ptg: variant.europe1_price_factory_ptg,
    unit_at_portion_price: variant.unit_at_portion_price,
    portion_raw_weight: variant.portion_raw_weight
  }));

  const builProperties = {
    ...properties,
    ...(selected_variant.inventory_quantity !== "" && selected_variant.inventory_quantity !== null && selected_variant.inventory_quantity !== undefined ? { '_max_inventory': selected_variant.inventory_quantity } : {}),
    ...(selected_variant.deposit_reference !== "" && selected_variant.deposit_reference !== null ? { '_deposit_reference': selected_variant.deposit_reference } : {})
  }

  let is_default_variant = true;
  if(product.variants.length > 1) is_default_variant = false;

  if(Object.keys(product).length){
    const selected_product_variant = product?.variants.filter(variant => variant.id === variantId);
    const line_key = `${variantId}:${generateUniqueStringWithTimestamp()}`;
    //console.log(`selected_product_variant >>`,selected_product_variant)
    LocalCart.addItem({
      id: variantId,
      key: line_key,
      product_id: product.id,
      variant_id: variantId,
      price: Math.round(selected_variant.variant_price),
      compare_at_price: Math.round(selected_variant.variant_compare_at_price),
      portion_price: selected_variant.customer_portion_price,
      price_per_kg: selected_variant.price_per_kg,
      line_price: Math.round(parseInt(selected_variant.variant_price) * quantity),
      final_line_price: Math.round(parseInt(selected_variant.variant_price) * quantity),
      discounted_price: Math.round(selected_variant.variant_price),
      quantity: quantity,
      handle: product.handle,
      title: product.title,
      sku: selected_product_variant[0]?.sku,
      image: product.featured_image,
      properties: builProperties,
      tags: product.tags,
      vendor: product.vendor,
      selected_variant: selected_product_variant[0]?.title,
      options: product.options,
      variants: extractedVariants,
      product_has_only_default_variant: is_default_variant,
      url: `/products/${product.handle}?variant=${variantId}`,
      variant_title: selected_product_variant[0]?.title,
      line_level_discount_allocations: [],
      line_level_total_discount: 0,
      requires_shipping: true,
      currency: Shopify?.currency?.active,
      metafields: {
        variant: {
          deposit_reference: selected_variant.deposit_reference,
          netto_weight_sales_unit: selected_variant.netto_weight_sales_unit,
          orderable: selected_variant.isAvailable,
          bonus_product: selected_variant.bonus_product,
          europe1_price_factory_ptg: selected_variant.europe1_price_factory_ptg,
          unit_at_portion_price: selected_variant.unit_at_portion_price,
          portion_raw_weight: selected_variant.portion_raw_weight,
          is_product_bulk: selected_variant.is_product_bulk
        },
        product: {
          product_identifier: data?.product_metafields?.identifier,
          nutritions: data?.product_metafields?.nutritions
        }
      }
    });

    //Update the quantity-input value for this variant inside a kios cached
    if (AssortmentCache) await AssortmentCache.patchCartQty(String(product.id), String(variantId), Number(quantity), line_key).catch(() => {});
  }
}


addItemsFromURL();